import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Menu,
  Typography,
  Divider,
  Tooltip,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Tune } from '@mui/icons-material';
import { DateRangeValue } from './DateRangeFilter';
import DateRangeFilter from './DateRangeFilter';
import ClientFilter from './ClientFilter';
import { useDemands } from '@contexts/DemandsContext';
import { useConfig } from '@contexts/ConfigContext';
import { apiService } from '@/services/api';
import { IQRCode } from '@/types';
import DateTypeToggle from './advanced-filters/DateTypeToggle';
import FilterMultiSelect from './advanced-filters/FilterMultiSelect';
import SimilarOnlySwitch from './advanced-filters/SimilarOnlySwitch';
import ActiveFilterChips from './advanced-filters/ActiveFilterChips';

export interface FilterConfig {
  // Filtros básicos sempre disponíveis
  showClientFilter?: boolean;
  showDateRangeFilter?: boolean;
  
  // Tipo de página para determinar opções de data
  pageType?: 'demands' | 'reports';
  
  // Filtros específicos de demandas
  showCategoryFilter?: boolean;
  showStatusFilter?: boolean;
  showPriorityFilter?: boolean;
  showQrCodeFilter?: boolean;
  showSimilarOnlyFilter?: boolean;
  /** Item 10: filtro por responsável (assigneeId) e por prestador (providerId) — não fazem
   * sentido em páginas sem esse conceito (ex.: Sugestões), então ficam de fora por padrão. */
  showAssigneeFilter?: boolean;
  showProviderFilter?: boolean;
  
  // Variante para mobile
  variant?: 'default' | 'mobile' | 'compact';
}

interface AdvancedFiltersProps {
  config: FilterConfig;
}

// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({ config }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = config.variant === 'compact' || (!isMobile && config.variant !== 'mobile');

  const {
    dateRange,
    setDateRange,
    reportsDateType,
    setReportsDateType,
    demandsDateType,
    setDemandsDateType,
    selectedCategories,
    setSelectedCategories,
    selectedStatuses,
    setSelectedStatuses,
    selectedQrCodeIdentifiers,
    setSelectedQrCodeIdentifiers,
    selectedPriorities,
    setSelectedPriorities,
    selectedAssigneeIds,
    setSelectedAssigneeIds,
    selectedProviderIds,
    setSelectedProviderIds,
    showSimilarOnly,
    setShowSimilarOnly,
    selectedClients,
    setSelectedClients,
  } = useDemands();

  const { config: appConfig } = useConfig();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [qrCodes, setQrCodes] = useState<IQRCode[]>([]);
  const [qrCodesLoading, setQrCodesLoading] = useState(false);
  // Usuários/prestadores são cadastros globais do tenant (não por condomínio) — busca uma única
  // vez, sem depender de selectedClients (diferente do QR code acima).
  const [users, setUsers] = useState<Array<{ _id: string; name: string }>>([]);
  const [providersList, setProvidersList] = useState<Array<{ _id: string; name: string }>>([]);

  const open = Boolean(anchorEl);

  const pageType = config.pageType || 'demands';
  const isReportsPage = pageType === 'reports';
  const currentDateType = isReportsPage ? reportsDateType : demandsDateType;
  
  // Garantir que currentDateType sempre tenha um valor válido e que corresponda às opções disponíveis
  const safeDateType = React.useMemo(() => {
    if (!currentDateType) {
      return 'createdAt';
    }
    // Validar se o valor está entre as opções disponíveis para esta página
    if (isReportsPage) {
      return (currentDateType === 'createdAt' || currentDateType === 'completedAt') 
        ? currentDateType 
        : 'createdAt';
    } else {
      return (currentDateType === 'createdAt' || currentDateType === 'estimatedCompletion') 
        ? currentDateType 
        : 'createdAt';
    }
  }, [currentDateType, isReportsPage]);

  // Buscar QR codes quando necessário (com debounce para evitar múltiplas requisições)
  React.useEffect(() => {
    if (!config.showQrCodeFilter || selectedClients.length === 0) {
      setQrCodes([]);
      return;
    }

    // Debounce: esperar um pouco antes de buscar para evitar múltiplas requisições
    const timeoutId = setTimeout(() => {
      const fetchQRCodes = async () => {
        setQrCodesLoading(true);
        try {
          const allQrCodes: IQRCode[] = [];
          for (const clientId of selectedClients) {
            try {
              const response = await apiService.getQRCodes(clientId);
              if (response.data.success && response.data.data) {
                allQrCodes.push(...response.data.data);
              }
            } catch (error) {
              // Silently fail
            }
          }
          const uniqueIdentifiers = new Set<string>();
          const uniqueQrCodes = allQrCodes.filter(qr => {
            if (uniqueIdentifiers.has(qr.identifier)) {
              return false;
            }
            uniqueIdentifiers.add(qr.identifier);
            return true;
          });
          setQrCodes(uniqueQrCodes.sort((a, b) => a.identifier.localeCompare(b.identifier)));
        } catch (error) {
          // Silently fail
        } finally {
          setQrCodesLoading(false);
        }
      };

      fetchQRCodes();
    }, 300); // 300ms de debounce

    return () => clearTimeout(timeoutId);
  }, [selectedClients, config.showQrCodeFilter]);

  // Usuários/prestadores: cadastro global do tenant, busca uma única vez (sem debounce/depender
  // de condomínio selecionado, diferente do QR code acima).
  React.useEffect(() => {
    if (!config.showAssigneeFilter) {
      setUsers([]);
      return;
    }
    let cancelled = false;
    apiService.getAssignableUsers().then((response) => {
      if (cancelled) return;
      const list = Array.isArray(response.data?.data) ? response.data.data : [];
      setUsers(list.map((u: { _id: string; name: string }) => ({ _id: u._id, name: u.name })));
    }).catch(() => {
      // Silently fail
    });
    return () => { cancelled = true; };
  }, [config.showAssigneeFilter]);

  React.useEffect(() => {
    if (!config.showProviderFilter) {
      setProvidersList([]);
      return;
    }
    let cancelled = false;
    apiService.getProviders().then((response) => {
      if (cancelled) return;
      const list = Array.isArray(response.data?.data) ? response.data.data : [];
      setProvidersList(list.map((p: { _id: string; name: string }) => ({ _id: p._id, name: p.name })));
    }).catch(() => {
      // Silently fail
    });
    return () => { cancelled = true; };
  }, [config.showProviderFilter]);

  const categories = useMemo(() => {
    return appConfig?.demandManagement?.categories?.filter(c => c.isActive) || [];
  }, [appConfig]);

  const priorities = useMemo(() => {
    return appConfig?.demandManagement?.priorities?.filter(p => p.isActive) || [];
  }, [appConfig]);

  const statusOptions = useMemo(() => {
    return appConfig?.demandManagement?.columns?.filter(c => c.isActive !== false) || [];
  }, [appConfig]);

  const qrCodeOptions = useMemo(
    () =>
      qrCodes.map((qr) => ({
        key: qr.identifier,
        name: qr.identifier,
        menuLabel: `${qr.identifier} ${qr.description ? `(${qr.description})` : ''}`,
      })),
    [qrCodes]
  );

  const assigneeOptions = useMemo(
    () => users.map((u) => ({ key: u._id, name: u.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );

  const providerOptions = useMemo(
    () => providersList.map((p) => ({ key: p._id, name: p.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [providersList]
  );

  // Contar filtros ativos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedClients.length > 0) count++;
    if (dateRange.type !== 'all') count++;
    if (selectedCategories?.length) count++;
    if (selectedStatuses?.length) count++;
    if (selectedPriorities?.length) count++;
    if (selectedQrCodeIdentifiers?.length) count++;
    if (selectedAssigneeIds?.length) count++;
    if (selectedProviderIds?.length) count++;
    if (showSimilarOnly) count++;
    return count;
  }, [selectedClients, dateRange.type, selectedCategories, selectedStatuses, selectedPriorities, selectedQrCodeIdentifiers, selectedAssigneeIds, selectedProviderIds, showSimilarOnly]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDateRangeChange = (value: DateRangeValue) => {
    setDateRange(value, pageType);
  };

  const handleDateTypeChange = React.useCallback((type: 'createdAt' | 'completedAt' | 'estimatedCompletion') => {
    // Garantir que o tipo está correto baseado na página
    if (isReportsPage) {
      if (type === 'createdAt' || type === 'completedAt') {
        setReportsDateType(type);
      } else {
        setReportsDateType('createdAt');
      }
    } else {
      if (type === 'createdAt' || type === 'estimatedCompletion') {
        setDemandsDateType(type);
      } else {
        setDemandsDateType('createdAt');
      }
    }
  }, [isReportsPage, setReportsDateType, setDemandsDateType]);

  const handleClearAllFilters = () => {
    setDateRange({ type: 'all', startDate: null, endDate: null, label: 'Todos os períodos' }, pageType);
    setSelectedCategories([]);
    setSelectedStatuses([]);
    setSelectedPriorities([]);
    setSelectedQrCodeIdentifiers([]);
    setSelectedAssigneeIds([]);
    setSelectedProviderIds([]);
    setShowSimilarOnly(false);
  };

  // Quando variant é 'mobile', renderizar versão expandida dentro do drawer
  // Quando variant é 'compact' e está em mobile, retornar null (drawer usa variant 'mobile')
  if (isMobile && config.variant === 'compact') {
    return null;
  }

  // Versão mobile expandida (dentro do drawer)
  if (config.variant === 'mobile') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Condomínio */}
        {config.showClientFilter !== false && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Condomínio
            </Typography>
            <ClientFilter />
          </Box>
        )}

        {/* Tipo de Data */}
        {config.showDateRangeFilter !== false && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Tipo de Data
            </Typography>
            <DateTypeToggle
              value={safeDateType}
              isReportsPage={isReportsPage}
              onChange={handleDateTypeChange}
              fullWidth
            />
          </Box>
        )}

        {/* Período */}
        {config.showDateRangeFilter !== false && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Período
            </Typography>
            <DateRangeFilter
              value={dateRange}
              onChange={handleDateRangeChange}
              showDateTypeSelector={false}
              dateTypeOptions={isReportsPage ? 'reports' : 'demands'}
              variant="mobile"
            />
          </Box>
        )}

        {/* Categoria */}
        {config.showCategoryFilter && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Categoria
            </Typography>
            <FilterMultiSelect
              labelId="category-filter-mobile-label"
              label="Categoria"
              options={categories}
              selected={selectedCategories}
              onChange={setSelectedCategories}
              fullWidth
            />
          </Box>
        )}

        {/* Situação */}
        {config.showStatusFilter && !isReportsPage && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Situação
            </Typography>
            <FilterMultiSelect
              labelId="status-filter-mobile-label"
              label="Situação"
              options={statusOptions}
              selected={selectedStatuses}
              onChange={setSelectedStatuses}
              fullWidth
            />
          </Box>
        )}

        {/* Prioridade */}
        {config.showPriorityFilter && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Prioridade
            </Typography>
            <FilterMultiSelect
              labelId="priority-filter-mobile-label"
              label="Prioridade"
              options={priorities}
              selected={selectedPriorities}
              onChange={setSelectedPriorities}
              fullWidth
            />
          </Box>
        )}

        {/* Responsável */}
        {config.showAssigneeFilter && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Responsável
            </Typography>
            <FilterMultiSelect
              labelId="assignee-filter-mobile-label"
              label="Responsável"
              options={assigneeOptions}
              selected={selectedAssigneeIds}
              onChange={setSelectedAssigneeIds}
              allLabel="Todos"
              fullWidth
            />
          </Box>
        )}

        {/* Prestador */}
        {config.showProviderFilter && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Prestador
            </Typography>
            <FilterMultiSelect
              labelId="provider-filter-mobile-label"
              label="Prestador"
              options={providerOptions}
              selected={selectedProviderIds}
              onChange={setSelectedProviderIds}
              allLabel="Todos"
              fullWidth
            />
          </Box>
        )}

        {/* QR Code */}
        {config.showQrCodeFilter && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              QR Code
            </Typography>
            <FilterMultiSelect
              labelId="qrcode-filter-mobile-label"
              label="QR Code"
              options={qrCodeOptions}
              selected={selectedQrCodeIdentifiers}
              onChange={setSelectedQrCodeIdentifiers}
              disabled={qrCodesLoading || selectedClients.length === 0}
              allLabel="Todos"
              fullWidth
            />
          </Box>
        )}

        {/* Apenas Similares */}
        {config.showSimilarOnlyFilter && !isReportsPage && (
          <Box>
            <SimilarOnlySwitch checked={showSimilarOnly} onChange={setShowSimilarOnly} />
          </Box>
        )}

        {/* Limpar Filtros */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            size="small"
            onClick={handleClearAllFilters}
            disabled={activeFiltersCount === 0}
            color="error"
          >
            Limpar Filtros
          </Button>
        </Box>
      </Box>
    );
  }

  if (isCompact) {
    // Versão compacta - chips + botão de menu
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* Chips de filtros ativos */}
        {activeFiltersCount > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <ActiveFilterChips
              selectedClients={selectedClients}
              onClearClients={() => setSelectedClients([])}
              dateRange={dateRange}
              pageType={pageType}
              onClearDateRange={() => setDateRange({ type: 'all', startDate: null, endDate: null, label: 'Todos os períodos' }, pageType)}
              statusOptions={statusOptions}
              selectedStatuses={selectedStatuses}
              onSetSelectedStatuses={setSelectedStatuses}
              categories={categories}
              selectedCategories={selectedCategories}
              onSetSelectedCategories={setSelectedCategories}
              priorities={priorities}
              selectedPriorities={selectedPriorities}
              onSetSelectedPriorities={setSelectedPriorities}
              selectedQrCodeIdentifiers={selectedQrCodeIdentifiers}
              onSetSelectedQrCodeIdentifiers={setSelectedQrCodeIdentifiers}
              assigneeOptions={assigneeOptions}
              selectedAssigneeIds={selectedAssigneeIds}
              onSetSelectedAssigneeIds={setSelectedAssigneeIds}
              providerOptions={providerOptions}
              selectedProviderIds={selectedProviderIds}
              onSetSelectedProviderIds={setSelectedProviderIds}
              showSimilarOnly={showSimilarOnly}
              onClearSimilarOnly={() => setShowSimilarOnly(false)}
            />
          </Stack>
        )}

        {/* Botão de filtros */}
        <Tooltip title="Filtros">
          <Button
            variant={activeFiltersCount > 0 ? 'contained' : 'outlined'}
            size="small"
            startIcon={<Tune />}
            onClick={handleMenuClick}
            sx={{
              minWidth: 'auto',
              height: 32,
              ...(activeFiltersCount > 0
                ? {
                    backgroundColor: '#21316a',
                    color: 'white',
                    '& .MuiSvgIcon-root': { color: 'white' },
                    '&:hover': { 
                      backgroundColor: '#1a2854',
                      color: 'white',
                      '& .MuiSvgIcon-root': { color: 'white' }
                    },
                    '&::after': {
                      content: `"${activeFiltersCount}"`,
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      backgroundColor: theme.palette.error.main,
                      color: 'white',
                      borderRadius: '50%',
                      width: 18,
                      height: 18,
                      fontSize: '0.7rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                    position: 'relative',
                  }
                : {
                    // Quando não há filtros ativos: borda e texto escuros (header claro)
                    borderColor: '#334155',
                    color: '#334155',
                    '& .MuiSvgIcon-root': {
                      color: 'inherit'
                    },
                    '&:hover': {
                      borderColor: '#21316a',
                      backgroundColor: 'rgba(33, 49, 106, 0.06)',
                      color: '#21316a'
                    }
                  }
              ),
            }}
          >
            Filtros
          </Button>
        </Tooltip>

        {/* Menu de filtros */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              minWidth: 300,
              maxWidth: 400,
              mt: 1,
            },
          }}
        >
          {/* Condomínio */}
          {config.showClientFilter !== false && (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Condomínio
              </Typography>
              <ClientFilter />
            </Box>
          )}

          {/* Tipo de Data */}
          {config.showDateRangeFilter !== false && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Tipo de Data
                </Typography>
                <DateTypeToggle
                  value={safeDateType}
                  isReportsPage={isReportsPage}
                  onChange={handleDateTypeChange}
                  fullWidth
                />
              </Box>
            </>
          )}

          {/* Período */}
          {config.showDateRangeFilter !== false && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Período
                </Typography>
                <Box sx={{ display: 'block', width: '100%' }}>
                  <DateRangeFilter
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    showDateTypeSelector={false}
                    dateTypeOptions={isReportsPage ? 'reports' : 'demands'}
                    variant="mobile"
                  />
                </Box>
              </Box>
            </>
          )}

          {/* Categoria */}
          {config.showCategoryFilter && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Categoria
                </Typography>
                <FilterMultiSelect
                  labelId="category-filter-label"
                  label="Categoria"
                  options={categories}
                  selected={selectedCategories}
                  onChange={setSelectedCategories}
                  fullWidth
                />
              </Box>
            </>
          )}

          {/* Situação */}
          {config.showStatusFilter && !isReportsPage && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Situação
                </Typography>
                <FilterMultiSelect
                  labelId="status-filter-label"
                  label="Situação"
                  options={statusOptions}
                  selected={selectedStatuses}
                  onChange={setSelectedStatuses}
                  fullWidth
                />
              </Box>
            </>
          )}

          {/* Prioridade */}
          {config.showPriorityFilter && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Prioridade
                </Typography>
                <FilterMultiSelect
                  labelId="priority-filter-label"
                  label="Prioridade"
                  options={priorities}
                  selected={selectedPriorities}
                  onChange={setSelectedPriorities}
                  fullWidth
                />
              </Box>
            </>
          )}

          {/* Responsável */}
          {config.showAssigneeFilter && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Responsável
                </Typography>
                <FilterMultiSelect
                  labelId="assignee-filter-label"
                  label="Responsável"
                  options={assigneeOptions}
                  selected={selectedAssigneeIds}
                  onChange={setSelectedAssigneeIds}
                  allLabel="Todos"
                  fullWidth
                />
              </Box>
            </>
          )}

          {/* Prestador */}
          {config.showProviderFilter && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Prestador
                </Typography>
                <FilterMultiSelect
                  labelId="provider-filter-label"
                  label="Prestador"
                  options={providerOptions}
                  selected={selectedProviderIds}
                  onChange={setSelectedProviderIds}
                  allLabel="Todos"
                  fullWidth
                />
              </Box>
            </>
          )}

          {/* QR Code */}
          {config.showQrCodeFilter && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  QR Code
                </Typography>
                <FilterMultiSelect
                  labelId="qrcode-filter-label"
                  label="QR Code"
                  options={qrCodeOptions}
                  selected={selectedQrCodeIdentifiers}
                  onChange={setSelectedQrCodeIdentifiers}
                  disabled={qrCodesLoading || selectedClients.length === 0}
                  allLabel="Todos"
                  fullWidth
                />
              </Box>
            </>
          )}

          {/* Apenas similares */}
          {config.showSimilarOnlyFilter && !isReportsPage && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <SimilarOnlySwitch checked={showSimilarOnly} onChange={setShowSimilarOnly} />
              </Box>
            </>
          )}

          <Divider />
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button 
              size="small" 
              onClick={handleClearAllFilters}
              disabled={activeFiltersCount === 0}
              color="error"
            >
              Limpar Filtros
            </Button>
            <Button size="small" onClick={handleMenuClose}>
              Fechar
            </Button>
          </Box>
        </Menu>
      </Box>
    );
  }

  // Versão expandida (desktop completo)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {config.showClientFilter !== false && <ClientFilter />}
      {config.showDateRangeFilter !== false && (
        <>
          <DateTypeToggle
            value={safeDateType}
            isReportsPage={isReportsPage}
            onChange={handleDateTypeChange}
            dense
          />
          <Box sx={{ display: 'inline-block', minWidth: 220 }}>
            <DateRangeFilter
              value={dateRange}
              onChange={handleDateRangeChange}
              showDateTypeSelector={false}
              dateTypeOptions={isReportsPage ? 'reports' : 'demands'}
            />
          </Box>
        </>
      )}
      {config.showCategoryFilter && (
        <FilterMultiSelect
          labelId="category-filter-label"
          label="Categoria"
          options={categories}
          selected={selectedCategories}
          onChange={setSelectedCategories}
          minWidth={180}
        />
      )}
      {config.showStatusFilter && !isReportsPage && (
        <FilterMultiSelect
          labelId="status-filter-label"
          label="Situação"
          options={statusOptions}
          selected={selectedStatuses}
          onChange={setSelectedStatuses}
          minWidth={180}
        />
      )}
      {config.showPriorityFilter && (
        <FilterMultiSelect
          labelId="priority-filter-label"
          label="Prioridade"
          options={priorities}
          selected={selectedPriorities}
          onChange={setSelectedPriorities}
          minWidth={180}
        />
      )}
      {config.showAssigneeFilter && (
        <FilterMultiSelect
          labelId="assignee-filter-label"
          label="Responsável"
          options={assigneeOptions}
          selected={selectedAssigneeIds}
          onChange={setSelectedAssigneeIds}
          allLabel="Todos"
          minWidth={180}
        />
      )}
      {config.showProviderFilter && (
        <FilterMultiSelect
          labelId="provider-filter-label"
          label="Prestador"
          options={providerOptions}
          selected={selectedProviderIds}
          onChange={setSelectedProviderIds}
          allLabel="Todos"
          minWidth={180}
        />
      )}
      {config.showQrCodeFilter && (
        <FilterMultiSelect
          labelId="qrcode-filter-label"
          label="QR Code"
          options={qrCodeOptions}
          selected={selectedQrCodeIdentifiers}
          onChange={setSelectedQrCodeIdentifiers}
          disabled={qrCodesLoading || selectedClients.length === 0}
          allLabel="Todos"
          minWidth={200}
        />
      )}
      {config.showSimilarOnlyFilter && !isReportsPage && (
        <SimilarOnlySwitch checked={showSimilarOnly} onChange={setShowSimilarOnly} labelFontSize="0.875rem" />
      )}
    </Box>
  );
};

export default AdvancedFilters;

